// components/ventas/SeleccionPlan.jsx
import React, { useState } from 'react';
import { Button, Alert, Input, Spin } from 'antd';
import {
  UserOutlined, IdcardOutlined, ArrowRightOutlined, ArrowLeftOutlined,
  GiftOutlined, TagOutlined, LoadingOutlined, CloseOutlined,
  CheckOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { useNotify } from '../../../../providers/NotificationProvider';
import CodigosManuales from '../../../../service/CodigosManuales';

export default function SeleccionPlan({ ventaData, updateVentaData, nextStep, prevStep }) {
  const notify = useNotify();
  const [planSeleccionado, setPlanSeleccionado] = useState(ventaData.planNombre || null);
  const [codigoInput, setCodigoInput]           = useState(ventaData.codigoDescuentoManual || '');
  const [aplicandoCodigo, setAplicandoCodigo]   = useState(false);
  const [descuentoManual, setDescuentoManual]   = useState(ventaData.descuentoManual || null);

  // Cada ítem de la venta: su taller y cuántos horarios lleva (el backend cobra el
  // precio del plan POR horario, más la matrícula del plan).
  const itemsVenta = ventaData.items?.length > 0
    ? ventaData.items.map(i => ({ taller: i.taller, nHorarios: i.horarioIds?.length || 1 }))
    : [{ taller: ventaData.taller, nHorarios: ventaData.horarioIds?.length || 1 }];
  const taller          = itemsVenta[0]?.taller;
  const esMult          = itemsVenta.length > 1;
  // Con varios talleres el plan se aplica a todos: solo se ofrecen los planes que
  // TODOS tienen. Antes se listaban los del primero y el cálculo fallaba en el resto.
  const planesDisponibles = (taller?.modalidadPago || []).filter(m =>
    itemsVenta.every(it => (it.taller?.modalidadPago || []).some(p => p.plan === m.plan)));
  const planesDescartados = esMult
    ? (taller?.modalidadPago || []).filter(m => !planesDisponibles.includes(m)).map(m => m.plan)
    : [];
  const esGratuito      = itemsVenta.every(it => !(it.taller?.modalidadPago || []).length);
  const esVecino        = ventaData.esVecino;
  const edadRaw         = ventaData.inscrito?.edad;
  const edad            = edadRaw === null || edadRaw === undefined || edadRaw === '' ? NaN : Number(edadRaw);
  const esAdultoMayor   = !isNaN(edad) && edad >= 60;

  const formatearPrecio = (p) =>
    new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', minimumFractionDigits: 0 }).format(p || 0);

  // ── Lógica de precio (espejo de calcularPrecioFinal en precioTaller.js) ──
  const calcularPrecioPlan = (modalidad) => {
    const vP = modalidad.valorParticular  || 0;
    const vA = modalidad.valorAdultoMayor || 0;
    const vV = modalidad.valorVecino      || 0;
    const tieneAM  = vA > 0;
    const tieneVec = vV > 0;

    if (esAdultoMayor && esVecino) {
      if (tieneAM)  return { precioFinal: vA, precioOriginal: vP, ahorro: vP - vA, tipo: 'adultoMayor', label: 'Adulto Mayor' };
      if (tieneVec) return { precioFinal: vV, precioOriginal: vP, ahorro: vP - vV, tipo: 'vecino',      label: 'Vecino'       };
      return                { precioFinal: vP, precioOriginal: null, ahorro: 0,    tipo: 'particular',  label: 'Particular'   };
    }
    if (esAdultoMayor && !esVecino)
      return { precioFinal: vP, precioOriginal: null, ahorro: 0, tipo: 'particular', label: 'Particular' };
    if (esVecino && tieneVec)
      return { precioFinal: vV, precioOriginal: vP, ahorro: vP - vV, tipo: 'vecino', label: 'Vecino' };
    return { precioFinal: vP, precioOriginal: null, ahorro: 0, tipo: 'particular', label: 'Particular' };
  };

  // Total estimado del plan para toda la venta: precio por horario × horarios +
  // matrícula, sumado por taller. El monto definitivo (con descuentos automáticos
  // y el código) lo calcula el backend en el paso siguiente.
  const totalPlan = (planKey) => {
    let total = 0, original = 0, horarios = 0;
    let base = null;
    for (const it of itemsVenta) {
      const m = (it.taller?.modalidadPago || []).find(p => p.plan === planKey);
      if (!m) return null;
      const p = calcularPrecioPlan(m);
      if (!base) base = p;
      const matricula = m.matricula || 0;
      total    += p.precioFinal * it.nHorarios + matricula;
      original += (p.precioOriginal ?? p.precioFinal) * it.nHorarios + matricula;
      horarios += it.nHorarios;
    }
    return { total, original, ahorro: Math.max(0, original - total), horarios, tipo: base?.tipo, label: base?.label };
  };

  const handleAplicarCodigo = async () => {
    if (!codigoInput.trim()) return;
    const codigo = codigoInput.trim().toUpperCase();
    const tallerId = taller?._id;
    setAplicandoCodigo(true);
    try {
      const res = await CodigosManuales.Validar(codigo, tallerId);
      if (res?.success && res?.data) {
        const descuento = res.data;
        setDescuentoManual(descuento);
        updateVentaData({ codigoDescuentoManual: codigo, descuentoManual: descuento });
        notify.success(`Código "${codigo}" aplicado: ${descuento.nombre}`);
      } else {
        notify.error('Código no encontrado o no válido para este taller');
      }
    } catch (error) {
      notify.error(error.message || 'Código inválido o no aplicable');
    } finally {
      setAplicandoCodigo(false);
    }
  };

  const handleQuitarCodigo = () => {
    setCodigoInput('');
    setDescuentoManual(null);
    updateVentaData({ codigoDescuentoManual: null, descuentoManual: null });
  };

  const handleContinuar = () => {
    if (!planSeleccionado && !esGratuito) {
      notify.warning('Selecciona un plan primero');
      return;
    }
    const planObj = planesDisponibles.find(p => p.plan === planSeleccionado);
    updateVentaData({ planNombre: esGratuito ? 'gratuito' : planSeleccionado, planDetalle: planObj || null });
    nextStep();
  };

  const duracionLabel = { mensual: '1 mes', trimestral: '3 meses', semestral: '6 meses', anual: '12 meses' };

  // ── Taller gratuito ────────────────────────────────────────────────────────
  if (esGratuito) {
    return (
      <div className="max-w-lg mx-auto space-y-4">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-6 py-5 border-b border-slate-100">
            <h3 className="font-bold text-slate-800 text-lg">Plan de <span className="hl-amarillo">pago</span></h3>
          </div>
          <div className="p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center
              justify-center mx-auto mb-4">
              <GiftOutlined className="text-blue-600 text-2xl" />
            </div>
            <h4 className="font-bold text-slate-800 text-xl mb-2">Taller gratuito</h4>
            <p className="text-slate-500 text-sm">
              Este taller no tiene costo. Continúa para confirmar la inscripción.
            </p>
            <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700
              font-bold text-2xl px-6 py-3 rounded-xl mt-4 border border-blue-200">
              $0
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <Button icon={<ArrowLeftOutlined />} onClick={prevStep} size="large">Anterior</Button>
          <Button
            type="primary" size="large" icon={<ArrowRightOutlined />}
            onClick={handleContinuar}
            className="h-11 px-8 font-semibold"
            style={{ background: '#feda2c', borderColor: '#feda2c', color: '#00266b' }}
          >
            Continuar
          </Button>
        </div>
      </div>
    );
  }

  // ── Taller de pago ─────────────────────────────────────────────────────────
  return (
    <div className="max-w-2xl mx-auto space-y-4">

      {/* ── Info del inscrito ──────────────────────────────────────────── */}
      {ventaData.inscrito && (
        <div className="bg-white border border-slate-200 rounded-xl px-5 py-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 bg-[#fffbea] border border-[#fde68a] rounded-full flex items-center
                justify-center">
                <UserOutlined className="text-[#5c4300]" />
              </div>
              <div>
                <div className="font-semibold text-slate-800 text-sm">
                  {ventaData.inscrito.nombre}
                </div>
                <div className="text-xs text-slate-400 mt-0.5">
                  {isNaN(edad) ? 'Sin fecha de nacimiento' : `${edad} años`}
                  {esAdultoMayor && (
                    <span className="ml-1.5 bg-purple-100 text-purple-700 px-1.5 py-0.5
                      rounded text-xs font-medium">Adulto Mayor</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {esVecino && (
                <span className="inline-flex items-center gap-1 bg-[#feda2c]
                  text-[#5c4300] text-xs font-semibold px-2.5 py-1 rounded-full">
                  <IdcardOutlined style={{ fontSize: 11 }} />
                  Vecino con descuento
                </span>
              )}
              {esMult && (
                <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700
                  text-xs font-semibold px-2.5 py-1 rounded-full">
                  {ventaData.items.length} talleres
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Multi-taller aviso ─────────────────────────────────────────── */}
      {esMult && (
        <Alert
          message={`El plan seleccionado se aplicará a los ${ventaData.items.length} talleres del carrito.`}
          description={planesDescartados.length > 0
            ? `No se muestran los planes que no tienen todos los talleres: ${planesDescartados.join(', ')}.`
            : undefined}
          type="info" showIcon
        />
      )}
      {!esGratuito && planesDisponibles.length === 0 && (
        <Alert
          type="error" showIcon
          message="Los talleres del carrito no comparten ningún plan de pago"
          description="Vuelve al primer paso y véndelos por separado."
        />
      )}

      {/* ── Planes ────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Selecciona el plan de pago
          </span>
        </div>

        <div className="p-4 space-y-2">
          {planesDisponibles.map((modalidad) => {
            const precio     = calcularPrecioPlan(modalidad);
            const total      = totalPlan(modalidad.plan);
            const isSelected = planSeleccionado === modalidad.plan;
            const tieneDescuento = precio.ahorro > 0;
            const variosHorarios = total && total.horarios > 1;

            return (
              <div
                key={modalidad.plan}
                role="radio"
                aria-checked={isSelected}
                tabIndex={0}
                onClick={() => setPlanSeleccionado(modalidad.plan)}
                onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && setPlanSeleccionado(modalidad.plan)}
                className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer
                  transition-all ${
                  isSelected
                    ? 'border-[#0047bb] bg-blue-50 shadow-[0_0_0_3px_#feda2c55]'
                    : 'border-slate-200 hover:border-[#0047bb]/40'
                }`}
              >
                {/* Radio */}
                <div className={`w-5 h-5 rounded-full border-2 flex items-center
                  justify-center flex-shrink-0 ${
                  isSelected ? 'border-[#0047bb] bg-[#0047bb]' : 'border-slate-300'
                }`}>
                  {isSelected && <div className="w-2 h-2 rounded-full bg-[#feda2c]" />}
                </div>

                {/* Info del plan */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold text-sm capitalize ${
                      isSelected ? 'text-[#00266b]' : 'text-slate-700'
                    }`}>
                      Plan {modalidad.plan}
                    </span>
                    <span className="text-xs text-slate-400">
                      {duracionLabel[modalidad.plan] || modalidad.plan}
                    </span>
                    {tieneDescuento && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        precio.tipo === 'adultoMayor'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-[#feda2c] text-[#5c4300]'
                      }`}>
                        {precio.label}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    {formatearPrecio(precio.precioFinal)} por horario
                    {variosHorarios && <> × {total.horarios} horarios</>}
                    {!esMult && modalidad.matricula > 0 && <> + {formatearPrecio(modalidad.matricula)} matrícula</>}
                    {esMult && <> · {itemsVenta.length} talleres</>}
                  </div>
                </div>

                {/* Total estimado */}
                <div className="text-right flex-shrink-0">
                  {total && total.ahorro > 0 && (
                    <div className="text-xs text-slate-400 line-through">
                      {formatearPrecio(total.original)}
                    </div>
                  )}
                  <div className={`text-xl font-bold ${isSelected ? 'text-[#00266b]' : 'text-slate-800'}`}>
                    {total ? formatearPrecio(total.total) : '—'}
                  </div>
                  {total && total.ahorro > 0 && (
                    <div className="text-xs text-blue-600 font-medium">
                      Ahorro: {formatearPrecio(total.ahorro)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex items-start gap-2 text-xs text-slate-500 px-1">
        <InfoCircleOutlined className="mt-0.5" />
        <span>
          Totales estimados. El monto final (descuentos automáticos y código especial incluidos)
          se confirma en el paso siguiente, antes de cobrar.
        </span>
      </div>

      {/* ── Código de descuento manual ─────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
            Código de descuento especial
          </span>
          <p className="text-xs text-slate-400 mt-0.5">
            Para casos especiales: bomberos, doctores, funcionarios municipales, etc.
          </p>
        </div>

        <div className="p-4">
          {descuentoManual ? (
            <div className="flex items-center justify-between bg-amber-50 border
              border-amber-200 rounded-xl px-4 py-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center
                  justify-center">
                  <TagOutlined className="text-amber-600" />
                </div>
                <div>
                  <div className="font-semibold text-amber-800 text-sm">
                    {ventaData.codigoDescuentoManual}
                  </div>
                  <div className="text-xs text-amber-600">
                    {descuentoManual.descripcion || 'Descuento aplicado'}
                  </div>
                </div>
              </div>
              <button
                onClick={handleQuitarCodigo}
                className="w-7 h-7 rounded-lg flex items-center justify-center
                  text-amber-500 hover:bg-amber-100 transition-colors"
              >
                <CloseOutlined style={{ fontSize: 12 }} />
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <Input
                value={codigoInput}
                onChange={e => setCodigoInput(e.target.value.toUpperCase())}
                placeholder="Ej: BOMBEROS2024"
                prefix={<TagOutlined className="text-slate-400" />}
                suffix={aplicandoCodigo ? <LoadingOutlined /> : null}
                onPressEnter={handleAplicarCodigo}
                className="font-mono"
              />
              <Button
                onClick={handleAplicarCodigo}
                loading={aplicandoCodigo}
                disabled={!codigoInput.trim()}
                icon={<CheckOutlined />}
                className="flex-shrink-0"
              >
                Aplicar
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ── Navegación ────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <Button icon={<ArrowLeftOutlined />} onClick={prevStep} size="large">
          Anterior
        </Button>
        <Button
          type="primary" size="large"
          icon={<ArrowRightOutlined />}
          onClick={handleContinuar}
          disabled={!planSeleccionado && !esGratuito}
          className="h-11 px-8 font-semibold"
          style={{ background: '#feda2c', borderColor: '#feda2c', color: '#00266b' }}
        >
          Continuar
        </Button>
      </div>
    </div>
  );
}
